/**
 * Cookie-backed storage for the site-wide consent decision.
 *
 * Why a cookie (and not localStorage): Jody spans two origins —
 * jody.travel (marketing) and app.jody.travel (this app). localStorage is
 * per-origin, so a choice made on one would not be visible to the other.
 * A cookie scoped to the parent domain `.jody.travel` IS shared across all
 * subdomains, so the visitor decides once and both sites honour it.
 *
 * In local dev (localhost) there is no `.jody.travel` parent, so we set a
 * host-only cookie (no domain attribute). Cross-subdomain sharing simply
 * doesn't apply in dev, which is expected.
 *
 * NOTE: keep this file in sync with the landing repo's copy
 * (jody-landing-page/src/consent/consentCookie.ts) — same name, format,
 * and domain logic, so the two sites read/write the same cookie.
 */

const COOKIE_NAME = 'jody_consent';
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

type StoredValue = 'accepted' | 'rejected';

// `.jody.travel` in production (shared across subdomains); undefined on
// localhost / preview hosts so the cookie is set host-only.
function cookieDomain(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const host = window.location.hostname;
  return host === 'jody.travel' || host.endsWith('.jody.travel')
    ? '.jody.travel'
    : undefined;
}

export function readConsentCookie(): StoredValue | null {
  if (typeof document === 'undefined') return null;
  try {
    const match = document.cookie
      .split('; ')
      .find((row) => row.startsWith(COOKIE_NAME + '='));
    if (!match) return null;
    const value = decodeURIComponent(match.slice(COOKIE_NAME.length + 1));
    return value === 'accepted' || value === 'rejected' ? value : null;
  } catch {
    return null;
  }
}

export function writeConsentCookie(value: StoredValue): void {
  if (typeof document === 'undefined') return;
  try {
    const domain = cookieDomain();
    const secure = window.location.protocol === 'https:' ? '; secure' : '';
    document.cookie =
      `${COOKIE_NAME}=${encodeURIComponent(value)}` +
      `; path=/; max-age=${ONE_YEAR_SECONDS}; samesite=lax` +
      (domain ? `; domain=${domain}` : '') +
      secure;
  } catch {
    // Cookies unavailable (rare); the decision still lives in React memory
    // for this session — the visitor just sees the banner again next time.
  }
}

export function clearConsentCookie(): void {
  if (typeof document === 'undefined') return;
  try {
    const domain = cookieDomain();
    document.cookie =
      `${COOKIE_NAME}=; path=/; max-age=0; samesite=lax` +
      (domain ? `; domain=${domain}` : '');
  } catch {
    /* no-op */
  }
}
